import type { User } from '@supabase/supabase-js';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { AuthContext } from './AuthContext';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fonction pour initialiser l'authentification
    const initializeAuth = async () => {
      try {
        // Récupérer la session active
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Erreur lors de la récupération de la session:', sessionError);
          setUser(null);
          setLoading(false);
          return;
        }

        if (session?.user) {
          setUser(session.user);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Erreur lors de l'initialisation de l'auth:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    // Initialiser l'authentification
    initializeAuth();

    // Écouter les changements d'authentification
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // La logique de récupération de mot de passe est maintenant gérée directement dans ResetPasswordPage

      if (session?.user) {
        setUser(session.user);

        // Mettre à jour last_active_at à chaque connexion ou refresh de token (~55 min)
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          supabase.auth
            .updateUser({ data: { last_active_at: new Date().toISOString() } })
            .catch(() => {}); // non bloquant
        }
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Note: La fonction ensureUserMembership a été temporairement désactivée
  // pour éviter les problèmes de RLS. Elle sera réimplémentée plus tard.

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    return { error, success: !error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();

      // Même en cas d'erreur (ex: session invalide), on force la déconnexion côté client
      setUser(null);

      // Nettoyer le localStorage et sessionStorage
      localStorage.clear();
      sessionStorage.clear();

      // Recharger la page pour s'assurer que tout est bien nettoyé
      window.location.href = '/login';

      return { error, success: !error };
    } catch (err) {
      console.error('Error during signOut:', err);
      // En cas d'erreur totale, forcer quand même la déconnexion
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
      return { error: err as any, success: false };
    }
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;

    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: redirectUrl,
    });

    return { error, success: !error };
  };

  const updatePassword = async (newPassword: string) => {
    // Vérifier la session avant la mise à jour
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      return { error: sessionError, success: false };
    }

    if (!sessionData.session) {
      return {
        error: { message: 'Session expirée' } as const,
        success: false,
      };
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    return { error, success: !error };
  };

  const value = {
    user,
    loading,
    signInWithPassword,
    signOut,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
